"use client";

import { useMemo, useRef, useState, type RefObject } from "react";
import { Heading } from "@astryxdesign/core/Heading";
import { Text } from "@astryxdesign/core/Text";
import { Badge } from "@astryxdesign/core/Badge";
import { Button } from "@astryxdesign/core/Button";
import { ResearchIcon } from "@/components/icons/research-icon";
import { getIndustryLabel } from "@/lib/research/industries";
import { getReportTypeLabel } from "@/lib/research/report-types";
import type { LandingCaseItem } from "@/components/landing/cases-view";
import { LandingCmsImage } from "@/components/landing/landing-cms-image";
import { LandingCanvasText } from "@/components/landing/landing-canvas-text";
import {
  landingTextProps,
  styledTextString,
} from "@/lib/landing/puck/styled-text";

function layoutClass(layout: "grid" | "stack"): string {
  if (layout === "stack") {
    return "flex max-w-[22rem] flex-col gap-5";
  }
  return "grid grid-cols-[repeat(auto-fill,minmax(min(100%,20rem),22rem))] justify-start gap-5";
}

function uniqueSorted(values: string[]): string[] {
  return [...new Set(values.filter(Boolean))].sort((a, b) =>
    a.localeCompare(b, "ru"),
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "rounded-full border px-3 py-1 text-xs transition-colors",
        active
          ? "border-primary bg-primary/15 text-primary"
          : "border-border text-secondary hover:bg-muted",
      ].join(" ")}
    >
      {label}
    </button>
  );
}

export function LandingCasesCarouselNav({
  onPrev,
  onNext,
}: {
  onPrev: () => void;
  onNext: () => void;
}) {
  return (
    <div className="flex shrink-0 gap-2">
      <CarouselNavButton
        direction="prev"
        label="Предыдущие кейсы"
        onClick={onPrev}
      />
      <CarouselNavButton
        direction="next"
        label="Следующие кейсы"
        onClick={onNext}
      />
    </div>
  );
}

function CarouselNavButton({
  direction,
  onClick,
  label,
}: {
  direction: "prev" | "next";
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[color-mix(in_srgb,var(--landing-accent,var(--color-text-accent))_25%,transparent)] bg-[color-mix(in_srgb,var(--landing-accent,var(--color-text-accent))_10%,var(--color-background-body))] text-[var(--landing-accent,var(--color-text-accent))] transition-colors hover:border-[var(--landing-accent,var(--color-text-accent))] hover:bg-[color-mix(in_srgb,var(--landing-accent,var(--color-text-accent))_16%,var(--color-background-body))]"
    >
      <ResearchIcon
        name={direction === "prev" ? "arrowLeft" : "arrowRight"}
        size="sm"
      />
    </button>
  );
}

function CaseCardContent({
  item,
  index,
  variant,
}: {
  item: LandingCaseItem;
  index: number;
  variant: "grid" | "carousel";
}) {
  if (variant === "carousel") {
    const reportHref =
      typeof item.pdfUrl === "string" && item.pdfUrl.trim()
        ? item.pdfUrl.trim()
        : "/reports/create";

    return (
      <div className="flex min-h-[10.75rem] flex-col gap-2.5">
        <LandingCanvasText
          field={`items.${index}.title`}
          value={item.title}
          label="Название"
        >
          <Heading
            level={3}
            className="text-[15px] font-bold leading-snug text-white sm:text-base"
            {...landingTextProps(item.title)}
          />
        </LandingCanvasText>
        <LandingCanvasText
          field={`items.${index}.summary`}
          value={item.summary ?? ""}
          label="Кратко"
          multiline
          hideIfEmpty
        >
          <Text
            size="sm"
            className="flex-1 text-sm font-medium leading-snug text-white/80"
            {...landingTextProps(item.summary)}
          />
        </LandingCanvasText>
        {!styledTextString(item.summary) ? <div className="flex-1" /> : null}
        <div className="shrink-0 pt-1">
          <a href={reportHref} target="_blank" rel="noreferrer">
            <Button label="Смотреть отчёт" variant="primary" size="sm" />
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <LandingCanvasText
        field={`items.${index}.title`}
        value={item.title}
        label="Название"
      >
        <Heading level={3} {...landingTextProps(item.title)} />
      </LandingCanvasText>
      {(item.reportTypes?.length || item.industries?.length) ? (
        <div className="flex flex-wrap gap-1.5">
          {(item.reportTypes ?? []).map((t) => (
            <Badge
              key={`t-${t}`}
              label={getReportTypeLabel(t)}
              variant="neutral"
            />
          ))}
          {(item.industries ?? []).map((t) => (
            <Badge
              key={`i-${t}`}
              label={getIndustryLabel(t)}
              variant="neutral"
            />
          ))}
        </div>
      ) : null}
      <LandingCanvasText
        field={`items.${index}.summary`}
        value={item.summary ?? ""}
        label="Кратко"
        multiline
        hideIfEmpty
      >
        <Text color="secondary" {...landingTextProps(item.summary)} />
      </LandingCanvasText>
      {item.pdfUrl ? (
        <div className="mt-auto pt-2">
          <a href={item.pdfUrl} target="_blank" rel="noreferrer">
            <Button label="Смотреть отчёт" variant="primary" size="sm" />
          </a>
        </div>
      ) : null}
    </div>
  );
}

function LandingCasesCarousel({
  items,
  scrollRef,
}: {
  items: LandingCaseItem[];
  scrollRef: RefObject<HTMLDivElement | null>;
}) {
  return (
    <div className="@container w-full min-w-0 overflow-hidden">
      <div
        ref={scrollRef}
        className="flex snap-x snap-mandatory gap-5 overflow-x-auto overscroll-x-contain scroll-smooth pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {items.map((item, index) => (
          <article
            key={item.id ?? `${styledTextString(item.title)}-${index}`}
            className="relative aspect-[3/4] w-[calc((100cqw-1.25rem)/1.12)] max-w-[320px] shrink-0 snap-start overflow-hidden rounded-2xl shadow-md sm:w-[calc((100cqw-2.5rem)/3)]"
          >
          {item.coverUrl ? (
            <LandingCmsImage
              src={item.coverUrl}
              alt={styledTextString(item.title)}
              width={640}
              height={853}
              fill
              sizes="(max-width: 640px) 90vw, 320px"
              className="object-cover"
            />
          ) : (
            <div className="absolute inset-0 bg-[var(--color-surface-muted)]" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />

          <div className="absolute inset-x-0 bottom-0 p-3 sm:p-4">
            <div className="min-h-[12.5rem] rounded-xl border border-white/20 bg-black/35 p-4 backdrop-blur-xl sm:min-h-[13rem] sm:p-5">
              <CaseCardContent item={item} index={index} variant="carousel" />
            </div>
          </div>
        </article>
      ))}
      </div>
    </div>
  );
}

export function useCasesCarouselScroll() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollByPage = (direction: -1 | 1) => {
    const el = scrollRef.current;
    if (!el) return;
    const card = el.querySelector("article");
    if (!card) return;
    const style = getComputedStyle(el);
    const gap = Number.parseFloat(style.columnGap || style.gap || "0") || 20;
    const step = card.getBoundingClientRect().width + gap;
    el.scrollBy({ left: direction * step, behavior: "smooth" });
  };
  return { scrollRef, scrollByPage };
}

/** Клиент: фильтры / сетка / карусель кейсов. */
export function LandingCasesCatalog({
  items,
  layout = "grid",
  scrollRef,
}: {
  items: LandingCaseItem[];
  layout?: "grid" | "stack" | "carousel";
  scrollRef?: RefObject<HTMLDivElement | null>;
}) {
  const typeOptions = useMemo(
    () => uniqueSorted(items.flatMap((i) => i.reportTypes ?? [])),
    [items],
  );
  const industryOptions = useMemo(
    () => uniqueSorted(items.flatMap((i) => i.industries ?? [])),
    [items],
  );

  const showTypeFilters = layout !== "carousel" && typeOptions.length > 1;
  const showIndustryFilters = layout !== "carousel" && industryOptions.length > 1;
  const showFilters = showTypeFilters || showIndustryFilters;

  const [activeType, setActiveType] = useState<string | null>(null);
  const [activeIndustry, setActiveIndustry] = useState<string | null>(null);

  const filtered = useMemo(() => {
    return items.filter((item) => {
      if (activeType && !(item.reportTypes ?? []).includes(activeType)) {
        return false;
      }
      if (activeIndustry && !(item.industries ?? []).includes(activeIndustry)) {
        return false;
      }
      return true;
    });
  }, [items, activeType, activeIndustry]);

  if (items.length === 0) {
    return <Text color="secondary">Пока нет опубликованных кейсов.</Text>;
  }

  if (layout === "carousel") {
    if (!scrollRef) {
      return <Text color="secondary">Карусель недоступна.</Text>;
    }
    if (filtered.length === 0) {
      return <Text color="secondary">Пока нет опубликованных кейсов.</Text>;
    }
    return <LandingCasesCarousel items={filtered} scrollRef={scrollRef} />;
  }

  return (
    <div className="flex w-full flex-col gap-6">
      {showFilters ? (
        <div className="flex w-full flex-col gap-3">
          {showTypeFilters ? (
            <div className="flex flex-col gap-2">
              <Text size="sm" color="secondary">
                Тип отчёта
              </Text>
              <div className="flex flex-wrap gap-2">
                <FilterChip
                  label="Все"
                  active={activeType === null}
                  onClick={() => setActiveType(null)}
                />
                {typeOptions.map((value) => (
                  <FilterChip
                    key={value}
                    label={getReportTypeLabel(value)}
                    active={activeType === value}
                    onClick={() =>
                      setActiveType((prev) => (prev === value ? null : value))
                    }
                  />
                ))}
              </div>
            </div>
          ) : null}
          {showIndustryFilters ? (
            <div className="flex flex-col gap-2">
              <Text size="sm" color="secondary">
                Отрасль
              </Text>
              <div className="flex flex-wrap gap-2">
                <FilterChip
                  label="Все"
                  active={activeIndustry === null}
                  onClick={() => setActiveIndustry(null)}
                />
                {industryOptions.map((value) => (
                  <FilterChip
                    key={value}
                    label={getIndustryLabel(value)}
                    active={activeIndustry === value}
                    onClick={() =>
                      setActiveIndustry((prev) =>
                        prev === value ? null : value,
                      )
                    }
                  />
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {filtered.length === 0 ? (
        <Text color="secondary">Нет отчётов по выбранным фильтрам.</Text>
      ) : (
        <div className={`w-full ${layoutClass(layout)}`}>
          {filtered.map((item, filteredIndex) => {
            const index = items.indexOf(item);
            const fieldIndex = index >= 0 ? index : filteredIndex;
            return (
            <article
              key={item.id ?? `${styledTextString(item.title)}-${filteredIndex}`}
              className="flex w-full max-w-[22rem] flex-col overflow-hidden rounded-2xl border border-border/60 bg-surface shadow-sm"
            >
              {item.coverUrl ? (
                <LandingCmsImage
                  src={item.coverUrl}
                  alt=""
                  width={640}
                  height={360}
                  sizes="(max-width: 640px) 100vw, 352px"
                  className="aspect-[16/9] h-auto w-full object-cover"
                />
              ) : null}
              <div className="flex flex-1 flex-col gap-3 p-5 sm:p-6">
                <CaseCardContent item={item} index={fieldIndex} variant="grid" />
              </div>
            </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
